import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar, Trash2, Pencil, X, Loader2, MapPin, Clock, FileText, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import API from '@/utils/api';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import { MODULE_COLORS } from '@/utils/moduleColors';

const EVENT_TYPES = [
  { value: 'general', label: 'General Event', color: 'bg-slate-100 text-slate-700' },
  { value: 'medical_appointment', label: 'Medical Appointment', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'birthday', label: 'Birthday', color: 'bg-pink-100 text-pink-700' },
  { value: 'anniversary', label: 'Anniversary', color: 'bg-rose-100 text-rose-700' },
  { value: 'reservation', label: 'Reservation', color: 'bg-blue-100 text-blue-700' },
  { value: 'car_maintenance', label: 'Car Maintenance', color: 'bg-orange-100 text-orange-700' },
  { value: 'membership', label: 'Membership/Renewal', color: 'bg-purple-100 text-purple-700' },
  { value: 'family', label: 'Family Event', color: 'bg-green-100 text-green-700' },
];

const CalendarEventDetail = ({ isOpen, onClose, event, onEventUpdated, onEventDeleted }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({});

  // Reset state when modal opens
  const handleOpen = () => {
    setIsEditing(false);
    setShowDeleteConfirm(false);
    if (event) {
      setFormData({
        title: event.title || '',
        event_type: event.event_type || 'general',
        date: event.date || '',
        time: event.time || '',
        location: event.location || '',
        notes: event.notes || '',
      });
    }
  };

  // If event changes, update form data
  if (event && formData.title === undefined) {
    handleOpen();
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEdit = () => {
    setFormData({
      title: event.title || '',
      event_type: event.event_type || 'general',
      date: event.date || '',
      time: event.time || '',
      location: event.location || '',
      notes: event.notes || '',
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFormData({
      title: event.title || '',
      event_type: event.event_type || 'general',
      date: event.date || '',
      time: event.time || '',
      location: event.location || '',
      notes: event.notes || '',
    });
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter an event title');
      return;
    }

    setIsSaving(true);

    try {
      const response = await API.put(`/calendar/events/${event.id}`, {
        title: formData.title.trim(),
        date: formData.date,
        time: formData.time || null,
        location: formData.location.trim() || null,
        notes: formData.notes.trim() || null,
      });

      toast.success('Event updated successfully');

      if (onEventUpdated) {
        onEventUpdated(response.data);
      }

      setIsEditing(false);
      onClose();
    } catch (error) {
      console.error('Failed to update event:', error);
      toast.error('Failed to update event', {
        description: error.response?.data?.detail || 'Please try again',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);

    try {
      await API.delete(`/calendar/events/${event.id}`);

      toast.success('Event deleted', {
        description: event.module_link ? 'Linked module record was also removed' : undefined,
      });

      if (onEventDeleted) {
        onEventDeleted(event.id);
      }

      onClose();
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('Failed to delete event', {
        description: error.response?.data?.detail || 'Please try again',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleGoToModule = () => {
    if (event.module && MODULE_COLORS[event.module]) {
      navigate(MODULE_COLORS[event.module].path);
      onClose();
    }
  };

  if (!event) return null;

  const eventType = EVENT_TYPES.find(t => t.value === event.event_type) || EVENT_TYPES[0];
  const hasModuleLink = event.module && event.module !== 'calendar' && MODULE_COLORS[event.module];
  const isCalendarEvent = event.is_calendar_event || event.created_from === 'quickadd' || event.created_from === 'manual';

  const formatTime = (timeStr) => {
    if (!timeStr) return null;
    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-white" data-testid="event-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-slate-800">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: event.color ? `${event.color}40` : '#e2e8f0',
                }}
              >
                <Calendar className="h-4 w-4" style={{ color: event.color || '#64748b' }} />
              </div>
              {isEditing ? 'Edit Event' : 'Event Details'}
            </div>
            {!isEditing && isCalendarEvent && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEdit}
                  className="h-8 w-8 p-0 text-slate-500 hover:text-blue-600"
                  data-testid="edit-event-btn"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="h-8 w-8 p-0 text-slate-500 hover:text-red-600"
                  data-testid="delete-event-btn"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        {showDeleteConfirm ? (
          <div className="py-4" data-testid="delete-confirm-dialog">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-slate-800">Delete Event?</h3>
                <p className="text-sm text-slate-500 mt-1">
                  This will permanently delete "{event.title}"
                  {hasModuleLink && (
                    <span className="block mt-1 text-amber-600">
                      The linked {MODULE_COLORS[event.module]?.name || event.module} record will also be deleted.
                    </span>
                  )}
                </p>
              </div>
              <div className="flex justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="border-slate-200"
                  data-testid="cancel-delete-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="confirm-delete-btn"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Event'
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : isEditing ? (
          <form className="space-y-4 mt-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="edit-title" className="text-slate-700">Event Title *</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="border-slate-200 focus:border-blue-400"
                data-testid="edit-event-title"
              />
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edit-date" className="text-slate-700">Date *</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  className="border-slate-200 focus:border-blue-400"
                  data-testid="edit-event-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-time" className="text-slate-700">Time</Label>
                <Input
                  id="edit-time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleChange('time', e.target.value)}
                  className="border-slate-200 focus:border-blue-400"
                  data-testid="edit-event-time"
                />
              </div>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="edit-location" className="text-slate-700">Location</Label>
              <Input
                id="edit-location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                className="border-slate-200 focus:border-blue-400"
                data-testid="edit-event-location"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="edit-notes" className="text-slate-700">Notes</Label>
              <Textarea
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className="border-slate-200 focus:border-blue-400 min-h-[80px] resize-none"
                data-testid="edit-event-notes"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelEdit}
                className="border-slate-200"
                data-testid="cancel-edit-btn"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !formData.title.trim()}
                className="bg-blue-500 hover:bg-blue-600 text-white"
                data-testid="save-edit-btn"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4 mt-4">
            {/* Title and Badge */}
            <div>
              <h3 className="text-xl font-semibold text-slate-800" data-testid="event-detail-title">
                {event.title}
              </h3>
              <div className="flex items-center gap-2 mt-2">
                <Badge className={cn("text-xs", eventType.color)}>
                  {eventType.label}
                </Badge>
                {hasModuleLink && (
                  <Badge
                    variant="outline"
                    className="text-xs cursor-pointer hover:bg-slate-100"
                    onClick={handleGoToModule}
                    style={{
                      borderColor: MODULE_COLORS[event.module]?.color,
                      color: MODULE_COLORS[event.module]?.dark,
                    }}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    {MODULE_COLORS[event.module]?.name}
                  </Badge>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3 text-slate-600">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span data-testid="event-detail-date">
                  {event.date ? format(parseISO(event.date), 'EEEE, MMMM d, yyyy') : 'No date'}
                </span>
              </div>

              {event.time && (
                <div className="flex items-center gap-3 text-slate-600">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span data-testid="event-detail-time">{formatTime(event.time)}</span>
                </div>
              )}

              {event.location && (
                <div className="flex items-center gap-3 text-slate-600">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  <span data-testid="event-detail-location">{event.location}</span>
                </div>
              )}

              {event.notes && (
                <div className="flex items-start gap-3 text-slate-600">
                  <FileText className="h-4 w-4 text-slate-400 mt-0.5" />
                  <span className="text-sm" data-testid="event-detail-notes">{event.notes}</span>
                </div>
              )}
            </div>

            {/* Module Link Action */}
            {hasModuleLink && (
              <Button
                variant="outline"
                onClick={handleGoToModule}
                className="w-full border-slate-200 hover:bg-slate-50"
                data-testid="go-to-module-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View in {MODULE_COLORS[event.module]?.name || event.module}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CalendarEventDetail;
